// What the "Restrictions" styling knows about one LM lane, one RM/OSMLR way, or one
// LR section of either: which modes are shut out (an access class) and whether it is
// an HOV lane (an HOV state). Both models are first read into the neutral model of
// tomtom-internal/orbis-lane-road-mapping-lib — per mode open / closed / unknown —
// so the same restriction gets the same class, and so the same stroke, from LM,
// RM and OSMLR. Pure, so the node tests reach it; util/restrictionStrokes.js draws it.
import { splitConditionalValue } from "./conditionalValues.mjs";
import { splitLaneList } from "./laneLists.mjs";
import { isOffsetSectionedValue } from "./stepSections.mjs";

// The modes both models can state (the lib's Mode). Alphabetical, which is what the
// closed-set keys of CLASS_BY_CLOSED are joined in.
const MODES = ["bus", "hgv", "motorcar", "taxi"];

const tagged = (value) => value !== undefined && value !== null;

// LM tokens onto those modes, MOST GENERAL FIRST (the lib's Mode.COVERED_BY_TOKEN):
// a specific statement overwrites the general one above it, so motor_vehicle=no +
// bus=yes is a bus lane whatever order the tags arrive in. psv is retired by the
// producer but read, as the lib reads it.
const LM_HIERARCHY = [
  ["vehicleRestrictionMotorVehicle", MODES],
  ["vehicleRestrictionPSV", ["bus", "taxi"]],
  ["vehicleRestrictionBus", ["bus"]],
  ["vehicleRestrictionHGV", ["hgv"]],
  ["vehicleRestrictionMotorcar", ["motorcar"]],
  ["vehicleRestrictionTaxi", ["taxi"]],
];

/**
 * One LM lane's access per mode, as the lib's LmRestrictions reads it. LM `yes` means
 * ALLOWED — an exception under a more general `no` — never "reserved for": the
 * Bay Area HOV lane is motor_vehicle=no + motorcar/bus/taxi=yes, i.e. no trucks. A
 * token other than yes/no (blank, a raw LR chain on styleBySection's fallback) states
 * nothing: a readable statement above it stands, and a mode with none is "unknown".
 * @param {Record<string, string|undefined>} tags — as returned by extractTags()
 * @returns {Record<string, "open"|"closed"|"unknown">}
 */
export function lmAccess(tags) {
  const read = {};
  const unreadable = new Set();
  LM_HIERARCHY.forEach(([field, modes]) => {
    const token = tags[field];
    if (!tagged(token)) return;
    const state = token === "no" ? "closed" : token === "yes" ? "open" : null;
    modes.forEach((mode) => {
      if (state) read[mode] = state;
      else unreadable.add(mode);
    });
  });
  return Object.fromEntries(
    MODES.map((mode) => [
      mode,
      read[mode] ?? (unreadable.has(mode) ? "unknown" : "open"),
    ]),
  );
}

const RM_ACCESS_FIELDS = {
  bus: "rm_vehicleAccessBus",
  hgv: "rm_vehicleAccessHgv",
  motorcar: "rm_vehicleAccessMotorcar",
  taxi: "rm_vehicleAccessTaxi",
};

// RM writes only `no`; permission is written by omission, a blank slot or no list at
// all (the lib's RmRestrictions). Any other token is "unknown".
const rmTokenAccess = (token) =>
  !token ? "open" : token === "no" ? "closed" : "unknown";

/**
 * One RM/OSMLR lane's access per mode, from that lane's own slot values.
 * @param {Record<string, string|undefined>} slot — one value per lane list, as laneSlots() gives
 * @returns {Record<string, "open"|"closed"|"unknown">}
 */
export function rmLaneAccess(slot) {
  return Object.fromEntries(
    MODES.map((mode) => [mode, rmTokenAccess(slot[RM_ACCESS_FIELDS[mode]])]),
  );
}

/**
 * Every lane's access, as many lanes as the longest access list. A raw LR chain is
 * not split into lanes (splitLaneList), so on styleBySection's fallback it says
 * nothing rather than painting the whole way from one stretch.
 * @param {Record<string, string|undefined>} tags — as returned by extractTags()
 * @returns {Array<Record<string, "open"|"closed"|"unknown">>}
 */
export function rmSlotAccess(tags) {
  const lists = MODES.map((mode) =>
    splitLaneList(tags[RM_ACCESS_FIELDS[mode]]),
  );
  const lanes = Math.max(0, ...lists.map((list) => list.length));
  return Array.from({ length: lanes }, (_, lane) =>
    Object.fromEntries(
      MODES.map((mode, i) => [mode, rmTokenAccess(lists[i][lane])]),
    ),
  );
}

// Classes are named by the closed set. "unknown" never counts as closed: a statement
// nobody could read must not colour a lane.
const CLASS_BY_CLOSED = {
  hgv: "noTrucks",
  "bus,hgv": "noBusNoTruck",
  "hgv,motorcar": "taxiAndBus",
  "hgv,motorcar,taxi": "busOnly",
  "bus,hgv,motorcar": "taxiOnly",
  "bus,motorcar,taxi": "trucksOnly",
  "bus,hgv,motorcar,taxi": "closed",
};

const closedModes = (access) => MODES.filter((m) => access[m] === "closed");

/**
 * @param {Record<string, string>} access — from lmAccess / rmLaneAccess
 * @returns {string|null} a key of ACCESS_CLASS_COLORS; null when nothing is closed
 */
export function accessClassOf(access) {
  const closed = closedModes(access);
  if (closed.length === 0) return null;
  return CLASS_BY_CLOSED[closed.join(",")] ?? "other";
}

export const ACCESS_CLASS_COLORS = {
  busOnly: "maroon",
  taxiAndBus: "blue",
  taxiOnly: "yellow",
  noTrucks: "purple",
  // Not CSS "brown", which is too close to maroon.
  noBusNoTruck: "#008080",
  trucksOnly: "#795548",
  closed: "darkgrey",
  // Every closed set the palette doesn't name: restricted — read the info panel.
  other: "darkgrey",
};

/**
 * @param {Record<string, string>|undefined} access
 * @returns {string|null}
 */
export function accessColorOf(access) {
  const cls = access ? accessClassOf(access) : null;
  return cls ? ACCESS_CLASS_COLORS[cls] : null;
}

// Tie-break between lanes that shut out as many modes.
const RANK = [
  "closed",
  "busOnly",
  "taxiOnly",
  "trucksOnly",
  "taxiAndBus",
  "noBusNoTruck",
  "noTrucks",
  "other",
];

function outranks(a, b) {
  const byCount = closedModes(a).length - closedModes(b).length;
  if (byCount !== 0) return byCount > 0;
  const rank = (access) => RANK.indexOf(accessClassOf(access) ?? "other");
  return rank(a) < rank(b);
}

/**
 * The lane a per-way stroke speaks for: the one shutting out the most modes, ties
 * broken by RANK. The Lanes view is where the other lanes show.
 * @param {Array<Record<string, string>>} accesses
 * @returns {Record<string, string>|undefined} undefined for no lanes
 */
export function mostRestrictive(accesses) {
  return accesses.reduce(
    (best, access) => (!best || outranks(access, best) ? access : best),
    undefined,
  );
}

// The values that mark an RM HOV lane: `designated` in the OSM-normalized layers, `yes`
// in the RM ones and in every conditional. Same set as the Lanes view's classifyLane.
const HOV_LANE_VALUES = ["yes", "designated"];

// One entry per lane. A raw LR chain only reaches here on styleBySection's
// whole-feature fallback; its offsets are peeled off so each slot is still compared
// exactly instead of `"0-1000#yes"` failing to equal "yes".
function slots(value) {
  const text = String(value);
  const body = isOffsetSectionedValue(text)
    ? text.replace(/(^|;)\s*\d+(?:-\d*)?#/g, "|") // "500#", "0-1000#", "1000-#"
    : text;
  return body.split("|").map((slot) => slot.trim());
}

/**
 * @param {Record<string, string|undefined>} tags — as returned by extractTags()
 * @returns {"active"|"conditional"|"empty"|null} active: some lane is HOV; conditional:
 *   some lane is HOV during a posted window; empty: an HOV tag is present but no lane
 *   is HOV (`|||`, `no|no`) — drawn so "tagged, says nothing" is visible; null: untagged
 */
export function hovState(tags) {
  const lanes = tags.rm_hovLanes;
  const conditional = tags.rm_hovLanesConditional;
  if (!tagged(lanes) && !tagged(conditional)) return null;
  if (tagged(lanes) && slots(lanes).some((s) => HOV_LANE_VALUES.includes(s))) {
    return "active";
  }
  if (
    tagged(conditional) &&
    slots(conditional).some((s) =>
      HOV_LANE_VALUES.includes(splitConditionalValue(s)?.value),
    )
  ) {
    return "conditional";
  }
  return "empty";
}

/**
 * LM `hov_lane` takes only `yes` (the lib's Hov). Any other value reads like RM's
 * `no|no` — an HOV tag that marks no HOV lane. LM has no time-conditional form.
 * @param {Record<string, string|undefined>} tags — as returned by extractTags()
 * @returns {"active"|"empty"|null}
 */
export function lmHovState(tags) {
  if (!tagged(tags.hovLane)) return null;
  return slots(tags.hovLane).includes("yes") ? "active" : "empty";
}

// Plain JSON: styleBySection merges neighbouring sections by JSON.stringify.
const restriction = (access, hov) => (access || hov ? { access, hov } : null);

/**
 * An RM/OSMLR way: its most restrictive lane's access colour, plus its HOV state.
 * @param {Record<string, string|undefined>} tags
 * @returns {{access: string|null, hov: string|null}|null} null when neither applies
 */
export function classifyRmRestriction(tags) {
  return restriction(
    accessColorOf(mostRestrictive(rmSlotAccess(tags))),
    hovState(tags),
  );
}

/**
 * An LM lane: its access colour, plus its HOV state.
 * @param {Record<string, string|undefined>} tags
 * @returns {{access: string|null, hov: string|null}|null} null when neither applies
 */
export function classifyLmRestriction(tags) {
  return restriction(accessColorOf(lmAccess(tags)), lmHovState(tags));
}
